'use client'

import { useEffect, useMemo, useRef, useState } from 'react'
import { GpBadge } from '@/components/ui/gp-badge';
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { X, Search, Loader2, Users } from 'lucide-react'
import { UserAvatar } from '@/components/ui/user-avatar'
import { useFocusTrap } from '@/lib/use-focus-trap'
import {
  getTournamentParticipants,
  type Participant,
} from '@/lib/tournament-registration'
import { buildApexLevelIconUrl } from '@/lib/apex'
import { getDisplayImageUrl } from '@/lib/image-upload'

function Avatar({ p }: { p: Participant }) {
  return (
    <UserAvatar
      url={p.avatar}
      alt={p.gamertag}
      size={36}
      className="ring-1 ring-white/10"
    />
  )
}

/** Referral, Apex rank, and Apex level badges shown next to a participant. */
function ParticipantBadges({ p }: { p: Participant }) {
  const levelIcon = buildApexLevelIconUrl(p.apex?.level)
  const rankFull = p.apex?.rankName
    ? `${p.apex.rankName}${p.apex.rankDiv ? ` ${p.apex.rankDiv}` : ''}`
    : null

  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {p.referrer && (
        <span
          title={`Referred by @${p.referrer.gamertag}`}
          aria-label={`Referred by @${p.referrer.gamertag}`}
          className="inline-flex size-5 items-center justify-center overflow-hidden rounded-md border border-white/10 bg-white/5"
        >
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={getDisplayImageUrl(p.referrer.avatar, 64) || '/images/logo-no-back.png'}
            alt=""
            className={
              p.referrer.avatar
                ? 'h-full w-full object-cover'
                : 'h-full w-full bg-white/10 object-contain p-[1px]'
            }
          />
        </span>
      )}
      {rankFull && p.apex?.rankImg && (
        <span title={rankFull} aria-label={`Rank ${rankFull}`} className="inline-flex items-center">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src={p.apex.rankImg} alt="" className="h-5 w-auto object-contain" />
        </span>
      )}
      {typeof p.apex?.level === 'number' && (
        <span
          title={`Level ${p.apex.level.toLocaleString()}`}
          aria-label={`Level ${p.apex.level.toLocaleString()}`}
          className="inline-flex items-center"
        >
          {levelIcon ? (
            // eslint-disable-next-line @next/next/no-img-element
            <img src={levelIcon} alt="" className="h-5 w-auto object-contain" />
          ) : (
            <span className="rounded-md bg-white/10 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-white/80">
              Lv {p.apex.level.toLocaleString()}
            </span>
          )}
        </span>
      )}
    </div>
  )
}

export function ParticipantsModal({
  tournamentId,
  gameSlug,
  open,
  onClose,
}: {
  tournamentId: string
  /** Drives which per-game stat badges (if any) are shown. */
  gameSlug?: string
  open: boolean
  onClose: () => void
}) {
  const pathname = usePathname()
  const localeSeg = pathname?.split('/')[1]
  const locale = localeSeg === 'es' ? 'es' : localeSeg === 'ja' ? 'ja' : 'en'

  const [participants, setParticipants] = useState<Participant[] | null>(null)
  const [query, setQuery] = useState('')
  const dialogRef = useRef<HTMLDivElement>(null)
  useFocusTrap(open, dialogRef)

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setParticipants(null)
    getTournamentParticipants(tournamentId, gameSlug).then((list) => {
      if (!cancelled) setParticipants(list)
    })
    return () => {
      cancelled = true
    }
  }, [open, tournamentId, gameSlug])

  // Close on Escape.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open, onClose])

  const filtered = useMemo(() => {
    if (!participants) return []
    const q = query.trim().toLowerCase()
    if (!q) return participants
    return participants.filter((p) => p.gamertag.toLowerCase().includes(q))
  }, [participants, query])

  if (!open) return null

  const confirmedCount = participants?.filter((p) => p.status === 'confirmed').length ?? 0
  const subCount = participants?.filter((p) => p.status === 'substitute').length ?? 0

  return (
    <div
      className="fixed inset-0 z-[100] flex items-end justify-center bg-black/70 p-0 backdrop-blur-sm sm:items-center sm:p-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Participants"
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        className="flex max-h-[85vh] w-full max-w-lg flex-col overflow-hidden rounded-t-2xl border border-border bg-card shadow-2xl outline-none sm:rounded-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-5 py-4">
          <div className="flex items-center gap-2">
            <Users className="size-5 text-primary" />
            <h2 className="text-base font-semibold">Participants</h2>
            {participants && (
              <span className="text-sm text-muted-foreground">
                {confirmedCount} confirmed
                {subCount > 0 && ` · ${subCount} substitute${subCount === 1 ? '' : 's'}`}
              </span>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label="Close"
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-white/5 hover:text-foreground"
          >
            <X className="size-5" />
          </button>
        </div>

        {/* Search */}
        <div className="border-b border-border px-5 py-3">
          <div className="flex items-center gap-2 rounded-xl border border-border bg-background px-3 py-2">
            <Search className="size-4 shrink-0 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search participants…"
              className="h-6 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            />
          </div>
        </div>

        {/* List */}
        <div className="min-h-0 flex-1 overflow-y-auto px-2 py-2">
          {participants === null ? (
            <div className="flex justify-center py-12 text-muted-foreground">
              <Loader2 className="size-5 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <p className="px-3 py-12 text-center text-sm text-muted-foreground">
              {participants.length === 0
                ? 'No participants yet.'
                : 'No participants match your search.'}
            </p>
          ) : (
            <ul>
              {filtered.map((p) => (
                <li key={p.userId}>
                  <Link
                    href={`/${locale}/profile/${encodeURIComponent(p.gamertag)}`}
                    className="flex items-center gap-3 rounded-xl px-3 py-2 transition-colors hover:bg-white/5"
                  >
                    <span className="w-6 shrink-0 text-right text-sm font-semibold tabular-nums text-muted-foreground">
                      {p.rank}
                    </span>
                    <Avatar p={p} />
                    <span className="flex min-w-0 flex-1 items-center gap-1.5">
                      <span className="truncate text-sm font-medium">{p.gamertag}</span>
                      <GpBadge userId={p.userId} gamertag={p.gamertag} size={14} />
                    </span>
                    <ParticipantBadges p={p} />
                    {p.status === 'substitute' ? (
                      <span className="shrink-0 rounded-full bg-amber-500/15 px-2 py-0.5 text-[11px] font-medium text-amber-300">
                        Substitute
                      </span>
                    ) : (
                      <span className="shrink-0 rounded-full bg-emerald-500/15 px-2 py-0.5 text-[11px] font-medium text-emerald-300">
                        Confirmed
                      </span>
                    )}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}
